import { LogoutOutlined, PowerSettingsNew } from "@mui/icons-material";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useMicrospecDynamicUIContext } from "../../GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../../Themes";
import DrawerListItems from "./DrawerListItems";

const DrawerCommonContent = ({ data, collapsed }) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const filterPredicate = ((value) => value.common);

    return (
        <Box
            sx={{
                px: 0.5,
                py: 0,
            }}
        >
            <List sx={{ py: 0 }}>
                {/* Common drawer items */}
                <DrawerListItems data={data} filterPredicate={filterPredicate} collapsed={collapsed} />
                
                {/* Enhanced logout button */}
                <Tooltip 
                    title={localizationProvider.translateByType({
                        type: "Internal",
                        text: "logout"
                    })} 
                    placement="right"
                    arrow
                >
                    <ListItemButton
                        key="logout" 
                        onClick={() => cloudProvider.signOut(currentPath)}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onMouseDown={() => setIsPressed(true)}
                        onMouseUp={() => setIsPressed(false)}
                        sx={{
                            borderRadius: '8px',
                            mx: 0.5,
                            mb: 0,
                            mt: 0.25,
                            minHeight: 42,
                            py: 0.25,
                            px: collapsed ? 0 : undefined,
                            justifyContent: collapsed ? 'center' : undefined,
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            
                            // Base styling with danger theme
                            background: (theme) => theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(211, 47, 47, 0.08) 100%)'
                                : 'linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, rgba(211, 47, 47, 0.05) 100%)',
                            
                            border: (theme) => theme.palette.mode === 'dark'
                                ? '1px solid rgba(244, 67, 54, 0.2)'
                                : '1px solid rgba(244, 67, 54, 0.15)',

                            // Hover effects
                            '&:hover': {
                                background: (theme) => theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(211, 47, 47, 0.15) 100%)'
                                    : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(211, 47, 47, 0.1) 100%)',
                                transform: 'translateX(4px)',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 6px 25px rgba(244, 67, 54, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                                    : '0 6px 25px rgba(244, 67, 54, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                            },

                            // Pressed state
                            '&:active': {
                                transform: 'translateX(2px) scale(0.98)',
                                background: (theme) => theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.2) 0%, rgba(211, 47, 47, 0.2) 100%)'
                                    : 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(211, 47, 47, 0.15) 100%)',
                            },

                            // Glass morphism effect
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                // backdropFilter: 'blur(8px)',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease',
                                pointerEvents: 'none',
                            }
                        }}
                    >
                        {/* Warning indicator */}
                        {!collapsed && <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 4,
                                height: '60%',
                                borderRadius: '0 4px 4px 0',
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.4)',
                                opacity: isHovered ? 1 : 0.6,
                                transition: 'opacity 0.3s ease',
                            }}
                        />}

                        {/* Icon with enhanced styling */}
                        <ListItemIcon
                            sx={{
                                minWidth: collapsed ? 'auto' : 40,
                                mr: collapsed ? 0 : undefined,
                                justifyContent: 'center',
                                color: (theme) => theme.palette.mode === 'dark' ? '#f44336' : '#d32f2f',
                                transition: 'all 0.2s ease',
                                '& .MuiSvgIcon-root': {
                                    fontSize: 22,
                                    filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.3))',
                                    transform: isPressed ? 'scale(0.9)' : 'scale(1)',
                                    transition: 'transform 0.1s ease',
                                }
                            }}
                        >
                            <PowerSettingsNew />
                        </ListItemIcon>

                        {/* Text with keyboard shortcut */}
                        {!collapsed && <ListItemText
                            primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>
                                        {localizationProvider.translateByType({
                                            type: "Internal",
                                            text: "logout"
                                        })}
                                    </span>
                                    {/* <Chip
                                        size="small"
                                        label="ESC"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            background: (theme) => theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'rgba(0, 0, 0, 0.08)',
                                            color: theme.palette.text.secondary,
                                            border: (theme) => theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255, 255, 255, 0.2)'
                                                : '1px solid rgba(0, 0, 0, 0.15)',
                                            opacity: isHovered ? 1 : 0.7,
                                            transition: 'opacity 0.3s ease',
                                        }}
                                    /> */}
                                </Box>
                            }
                            sx={{
                                display: "flex", 
                                justifyContent: "flex-start",
                                '& .MuiListItemText-primary': {
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    color: (theme) => theme.palette.mode === 'dark' ? '#f44336' : '#d32f2f',
                                    transition: 'all 0.2s ease',
                                }
                            }}
                        />}

                        {/* Animated logout arrow */}
                        {!collapsed && <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                background: (theme) => theme.palette.mode === 'dark'
                                    ? 'rgba(244, 67, 54, 0.15)'
                                    : 'rgba(244, 67, 54, 0.1)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                transform: isHovered ? 'translateX(3px) scale(1.05)' : 'translateX(0) scale(1)',
                                opacity: isHovered ? 1 : 0.8,
                            }}
                        >
                            <LogoutOutlined
                                sx={{
                                    fontSize: 14,
                                    color: (theme) => theme.palette.mode === 'dark' ? '#f44336' : '#d32f2f',
                                }}
                            />
                        </Box>}
                    </ListItemButton>
                </Tooltip>
            </List>
        </Box>
    );
};

export default DrawerCommonContent;